import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, endpoints, type IntegrationCard } from "../api";
import { useAuth } from "../auth";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Spinner, InlineSpinner } from "../components/ui/spinner";

const MINT = "#aaffdc";

export default function Settings() {
  const { active } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["integrations", active?.slug],
    queryFn: () => endpoints.integrations(active!.slug),
    enabled: !!active,
  });

  if (isLoading) return <div className="p-8"><Spinner /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6 pb-12 px-8 pt-8">
      <div>
        <h1 className="text-foreground uppercase" style={{ fontSize: "20px", letterSpacing: "0.02em" }}>Settings</h1>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Per-workspace integrations. Secrets are encrypted at rest with AES-256-GCM and never sent back to the browser.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data.map((card) => (
          <IntegrationFormCard key={card.provider} card={card} />
        ))}
      </div>
    </div>
  );
}

function IntegrationFormCard({ card }: { card: IntegrationCard }) {
  const { active } = useAuth();
  const qc = useQueryClient();
  const [config, setConfig] = useState<Record<string, string>>(card.config ?? {});
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState(card.enabled);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    setConfig(card.config ?? {});
    setEnabled(card.enabled);
  }, [card.config, card.enabled]);

  const save = useMutation({
    mutationFn: () =>
      endpoints.saveIntegration(active!.slug, card.provider, {
        config,
        secrets: Object.keys(secrets).length ? secrets : undefined,
        enabled,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["integrations", active?.slug] });
      setSecrets({});
      setSavedAt(res.updated_at);
    },
  });

  const remove = useMutation({
    mutationFn: () => endpoints.removeIntegration(active!.slug, card.provider),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["integrations", active?.slug] });
      setConfig({});
      setSecrets({});
      setEnabled(false);
      setSavedAt(null);
    },
  });

  const err = save.error instanceof ApiError ? save.error.message : save.error?.message;
  const removeErr = remove.error instanceof ApiError ? remove.error.message : remove.error?.message;

  const isConfigured = card.publicFields.some((f) => card.config[f.key]) || card.hasSecrets;

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-foreground uppercase" style={{ fontSize: "13px", letterSpacing: "0.02em" }}>{card.label}</h2>
            <p className="mt-1 text-xs" style={{ color: "#adaaaa" }}>{card.description}</p>
          </div>
          {isConfigured && enabled && (
            <span style={{ backgroundColor: "#262626", color: MINT, padding: "2px 8px", borderRadius: 4, fontSize: "0.7rem", fontWeight: 700 }}>
              active
            </span>
          )}
        </div>

        <div className="space-y-3">
          {card.publicFields.map((f) => (
            <Field
              key={f.key}
              label={f.label}
              hint={f.hint}
              value={config[f.key] ?? ""}
              onChange={(v) => setConfig((c) => ({ ...c, [f.key]: v }))}
            />
          ))}
          {card.secretFields.map((f) => (
            <SecretField
              key={f.key}
              label={f.label}
              hint={f.hint}
              isSet={f.isSet}
              value={secrets[f.key] ?? ""}
              onChange={(v) => setSecrets((s) => ({ ...s, [f.key]: v }))}
              onClear={() => setSecrets((s) => ({ ...s, [f.key]: "" }))}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
          <label className="flex items-center gap-2 text-xs" style={{ color: "#adaaaa" }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="accent-[#c57eff]"
            />
            enabled
          </label>
          <div className="flex items-center gap-2">
            {isConfigured && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (confirm(`Remove ${card.label} configuration?`)) remove.mutate();
                }}
                disabled={remove.isPending}
              >
                {remove.isPending ? "Removing…" : "Remove"}
              </Button>
            )}
            <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? <><InlineSpinner size={12} /> Saving…</> : "Save"}
            </Button>
          </div>
        </div>

        {err && <p className="text-xs text-destructive">{err}</p>}
        {removeErr && <p className="text-xs text-destructive">{removeErr}</p>}
        {savedAt && !save.isPending && !err && (
          <p className="text-xs" style={{ color: MINT }}>Saved {new Date(savedAt).toLocaleString()}</p>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, hint, value, onChange }: {
  label: string; hint?: string; value: string; onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}{hint && <span className="ml-1 normal-case tracking-normal" style={{ color: "#666" }}>({hint})</span>}
      </label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function SecretField({ label, hint, isSet, value, onChange, onClear }: {
  label: string; hint?: string; isSet: boolean; value: string;
  onChange: (v: string) => void; onClear: () => void;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {label}{hint && <span className="ml-1 normal-case tracking-normal" style={{ color: "#666" }}>({hint})</span>}
        </label>
        {isSet && !value && (
          <span className="text-[10px]" style={{ color: MINT }}>•••••• stored</span>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          type={show ? "text" : "password"}
          value={value}
          placeholder={isSet ? "leave blank to keep existing" : ""}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1"
        />
        <Button type="button" size="sm" variant="ghost" onClick={() => setShow((s) => !s)}>
          {show ? "Hide" : "Show"}
        </Button>
        {value && (
          <Button type="button" size="sm" variant="ghost" onClick={onClear}>Clear</Button>
        )}
      </div>
    </div>
  );
}
